import { call, fork, put, select, takeLatest } from "redux-saga/effects";
import { Set } from "immutable";
import { eventChannel } from "redux-saga";
import axios from "axios";
import {
  ADD_NOTIFICATION,
  LOGIN_ERROR,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGOUT,
  LOGOUT_REQUEST,
  SAVE_USER_INFO,
  SAVE_USER_ROLES,
  SAVE_USER_TOKEN,
  SAVE_USER_MODULES,
} from "../actions/actionConstants";
import { setConfirmNavigation } from "../actions/UiActions";
import { login, logout, removeChatJwtToken, removeToken, saveUser } from "../utils/users";
import { identifyPostHogUser, resetPostHog } from "../utils/services/posthog/posthog";
import Data from "../utils/data";

function transformObject(obj) {
  const { name, roles, sub, imageUrl, organizationId, shopId, isEmployee, id } = obj;

  return {
    id,
    roles,
    name: name ?? "",
    email: sub ?? "",
    image: imageUrl ?? "",
    organization_id: organizationId,
    shop_id: shopId ?? 0,
    isEmployee
  };
}

function* authorize(action) {
  const {
    resolve,
    reject,
    formValues: {
      email,
      password,
      remember_me: rememberMe,
      notification_token,
    },
  } = action;
  try {
    const { refresh: token, userInfo } = yield call(
      login,
      email,
      password,
      rememberMe,
      notification_token
    );
    const userData = transformObject(userInfo);

    yield put({ type: LOGIN_SUCCESS });
    yield put({ type: SAVE_USER_TOKEN, token });
    yield put({ type: SAVE_USER_INFO, userInfo: userData });
    yield put({ type: SAVE_USER_ROLES, userRoles: Set(userData.roles) });
    identifyPostHogUser(userData);

    // Fetch subscription info to get modules for menu filtering
    try {
      const subscriptionData = yield call(Data.getSubscriptionInfo);

      // Dispatch modules to filter menu
      if (subscriptionData && subscriptionData.services) {
        console.log("Subscription data received:", subscriptionData.services.length, "services");
        yield put({ type: SAVE_USER_MODULES, services: subscriptionData.services });
      } else {
        console.warn("No services found in subscription data");
      }
    } catch (subscriptionError) {
      console.error("Failed to fetch subscription info:", subscriptionError);
      // Continue login flow even if subscription fetch fails
    }

    saveUser(token, rememberMe);
    localStorage.setItem("userInfo", JSON.stringify(userData));
    resolve(`Welcome ${userData.name}`);
  } catch (error) {
    yield put({ type: LOGIN_ERROR, error });
    reject(error.response?.data);
  }
}

export function* handleLogout(_, forced = false) {
  if (!forced) {
    const confirmNavigation = yield select((state) =>
      state.getIn(["ui", "confirmNavigation"])
    );
    const canLogout = !confirmNavigation || window.confirm("changes not saved, leave anyway?");
    if (!canLogout) return;
    try {
      yield call(logout);
    } catch {
      // The server refuses this call once the session is expired or stripped of
      // its permissions, which is exactly when people reach for Log Out.
      // Signing out on this device clears local credentials either way.
    }
  }
  yield call(removeToken);
  yield call(removeChatJwtToken);
  resetPostHog();
  localStorage.removeItem("userInfo");
  yield put(setConfirmNavigation(false));
  yield put({ type: LOGOUT });
}

function* saveProfile() {
  const loggedIn = yield select((state) =>
    state.getIn(["users", "user", "loggedIn"])
  );

  if (loggedIn) {
    try {
      const userInfo = yield call(Data.getUserInfo);
      yield put({ type: SAVE_USER_INFO, userInfo });
      yield put({ type: SAVE_USER_ROLES, userRoles: Set(userInfo.roles) });
      identifyPostHogUser(userInfo);

      // Fetch subscription info for module-based menu filtering on page refresh
      try {
        const subscriptionData = yield call(Data.getSubscriptionInfo);
        if (subscriptionData && subscriptionData.services) {
          yield put({ type: SAVE_USER_MODULES, services: subscriptionData.services });
        }
      } catch (subscriptionError) {
        console.error("Failed to fetch subscription info on refresh:", subscriptionError);
      }
    } catch {
      yield put({ type: LOGOUT });
    }
  }
}

// Only trigger forced logout when core auth endpoints return 401.
// Other endpoints (chat analytics, subscription, etc.) may 401 for
// permission reasons without meaning the session is invalid.
const AUTH_LOGOUT_PATHS = ["/user/info", "/token"];

const createUnauthorizedChannel = () => eventChannel((emitter) => {
  const interceptor = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const url = error.config?.url || "";
        const isAuthEndpoint = AUTH_LOGOUT_PATHS.some((p) => url.includes(p));
        if (isAuthEndpoint) {
          emitter(401);
        }
      }
      return Promise.reject(error);
    }
  );

  return () => {
    axios.interceptors.response.eject(interceptor);
  };
});

function* dispatchLogout() {
  const loggedIn = yield select((state) =>
    state.getIn(["users", "user", "loggedIn"])
  );
  if (loggedIn) {
    yield call(handleLogout, {}, true);
    yield put({
      type: ADD_NOTIFICATION,
      message: "You were signed out, please sign-in again.",
    });
  }
}

function* handleUnauthorized() {
  const unauthorizedChannel = yield call(createUnauthorizedChannel);
  yield takeLatest(unauthorizedChannel, dispatchLogout);
}

export default function* loginSaga() {
  yield fork(handleUnauthorized);
  yield fork(saveProfile);
  yield takeLatest(LOGIN_REQUEST, authorize);
  yield takeLatest(LOGOUT_REQUEST, handleLogout);
}
